import {
  DEFAULT_APPEARANCE_PREFS,
  loadAppearancePrefs,
  saveAppearancePrefs,
  type AppearancePrefs,
  type UiTheme,
} from './appearancePrefs.ts';

let currentThemePreference: UiTheme = DEFAULT_APPEARANCE_PREFS.theme;
let systemListener: ((event: MediaQueryListEvent) => void) | null = null;
let systemMediaQuery: MediaQueryList | null = null;

export function resolveEffectiveTheme(theme: UiTheme): 'dark' | 'light' {
  if (theme === 'light') return 'light';
  if (theme === 'dark') return 'dark';
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

function syncEffectiveThemeAttribute() {
  document.documentElement.dataset.uiThemeEffective = resolveEffectiveTheme(currentThemePreference);
}

function detachSystemThemeListener() {
  if (systemMediaQuery && systemListener) {
    systemMediaQuery.removeEventListener('change', systemListener);
    systemListener = null;
  }
}

function attachSystemThemeListener() {
  if (typeof window === 'undefined') return;

  if (!systemMediaQuery) {
    systemMediaQuery = window.matchMedia('(prefers-color-scheme: light)');
  }

  if (systemListener) return;

  systemListener = () => {
    if (currentThemePreference === 'system') {
      syncEffectiveThemeAttribute();
    }
  };

  systemMediaQuery.addEventListener('change', systemListener);
}

function bindThemePreference(theme: UiTheme) {
  currentThemePreference = theme;
  syncEffectiveThemeAttribute();

  if (theme === 'system') {
    attachSystemThemeListener();
  } else {
    detachSystemThemeListener();
  }
}

export function applyAppearanceToDocument(prefs: AppearancePrefs) {
  const root = document.documentElement;
  root.style.removeProperty('--st-accent');
  root.style.removeProperty('--nn-accent');
  root.dataset.uiTheme = prefs.theme;
  root.dataset.uiDensity = prefs.density;
  root.dataset.reduceMotion = prefs.reduceMotion ? 'true' : 'false';
  bindThemePreference(prefs.theme);
}

export function persistAppearancePrefs(userId: number, prefs: AppearancePrefs) {
  saveAppearancePrefs(userId, prefs);
  applyAppearanceToDocument(prefs);
}

export function initAppearanceTheme(userId: number | null | undefined) {
  const prefs = userId != null ? loadAppearancePrefs(userId) : { ...DEFAULT_APPEARANCE_PREFS };
  applyAppearanceToDocument(prefs);
}

export function resetAppearanceTheme() {
  applyAppearanceToDocument({ ...DEFAULT_APPEARANCE_PREFS });
}
